import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ArrowRightIcon, BuildingOffice2Icon, ClockIcon } from "@heroicons/react/24/outline";
import RoleOnly from "../../../routes/roleOnly";
import { fetchCompany } from "../../../api/company";
import { fetchHourSettings } from "../../../api/hour";
import { fetchAbsenceTypes } from "../../../api/absence/type";
import { fetchWorkSchedules } from "../../../api/workSchedules";
import { companyProfileIssues, hoursSetupIssues } from "./setupGuideStatus";

/**
 * Forsiden (dashboard): viser kun kort når admin har konkrete oppgaver i firma- eller timer-oppsett.
 */
export const DashboardAdminSetupAlerts = () => {
  const { t } = useTranslation();
  const currentCompany = useSelector((state) => state.session.currentCompany);

  const hoursModuleOn =
    !Array.isArray(currentCompany?.active_modules) ||
    currentCompany.active_modules.length === 0 ||
    currentCompany.active_modules.includes("hours");

  const [loading, setLoading] = useState(true);
  const [companyRecord, setCompanyRecord] = useState(null);
  const [hoursState, setHoursState] = useState({
    hourSettings: null,
    absenceTypeCount: null,
    workScheduleMeta: null,
    loadError: false,
  });

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      try {
        const cRes = await fetchCompany();
        if (cancelled) return;
        const cNorm = cRes?.data !== undefined ? cRes.data : cRes;
        const companyOk = cNorm && typeof cNorm === "object" && cNorm.id != null && !cNorm.errors;
        setCompanyRecord(companyOk ? cNorm : null);

        if (!hoursModuleOn) {
          setHoursState({
            hourSettings: null,
            absenceTypeCount: null,
            workScheduleMeta: null,
            loadError: false,
          });
          return;
        }

        try {
          const [hsRes, atRes] = await Promise.all([
            fetchHourSettings(),
            fetchAbsenceTypes({ page: 1, per_page: 1, sortBy: "created_at", sort: "DESC" }),
          ]);
          if (cancelled) return;
          const hsData =
            hsRes?.data?.data !== undefined
              ? hsRes.data.data
              : hsRes?.data !== undefined
                ? hsRes.data
                : {};
          const total = atRes?.totalCount ?? atRes?.data?.totalCount;
          let wsMeta = null;
          try {
            const wsRes = await fetchWorkSchedules();
            wsMeta = wsRes?.meta && typeof wsRes.meta === "object" ? wsRes.meta : null;
          } catch {
            wsMeta = null;
          }
          if (cancelled) return;
          setHoursState({
            hourSettings: typeof hsData === "object" ? hsData : {},
            absenceTypeCount: typeof total === "number" ? total : null,
            workScheduleMeta: wsMeta,
            loadError: false,
          });
        } catch {
          if (!cancelled) {
            setHoursState({
              hourSettings: null,
              absenceTypeCount: null,
              workScheduleMeta: null,
              loadError: true,
            });
          }
        }
      } catch {
        if (!cancelled) setCompanyRecord(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [hoursModuleOn, currentCompany?.id]);

  const companyIssues = useMemo(() => companyProfileIssues(companyRecord), [companyRecord]);
  const hoursIssues = useMemo(
    () =>
      hoursModuleOn
        ? hoursSetupIssues({
            hourSettings: hoursState.hourSettings,
            absenceTypeCount: hoursState.absenceTypeCount,
            workScheduleMeta: hoursState.workScheduleMeta,
            loadError: hoursState.loadError,
          })
        : [],
    [hoursModuleOn, hoursState]
  );

  const showCompany = companyIssues.length > 0;
  const showHours = hoursModuleOn && hoursIssues.length > 0 && !hoursState.loadError;

  if (loading || (!showCompany && !showHours)) {
    return null;
  }

  return (
    <RoleOnly role="admin">
      <div className="space-y-3">
        {showCompany ? (
          <Link
            to="/dashboard/guide/company#company-setup-status"
            className="flex flex-col gap-3 rounded-2xl border border-amber-200 bg-amber-50/50 px-4 py-3 shadow-sm transition hover:border-amber-300 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-amber-800 shadow-sm">
                <BuildingOffice2Icon className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-900">
                  {t("dashboard.company_guide.alert_company_title")}
                </div>
                <div className="text-xs text-slate-600">
                  {t("dashboard.company_guide.alert_company_subtitle", { count: companyIssues.length })}
                </div>
              </div>
            </div>
            <span className="inline-flex shrink-0 items-center gap-1 text-sm font-semibold text-slate-900 underline decoration-slate-300 underline-offset-4">
              {t("dashboard.company_guide.alert_cta")}
              <ArrowRightIcon className="h-4 w-4" />
            </span>
          </Link>
        ) : null}

        {showHours ? (
          <Link
            to="/dashboard/guide/hours#setup-status"
            className="flex flex-col gap-3 rounded-2xl border border-amber-200 bg-amber-50/50 px-4 py-3 shadow-sm transition hover:border-amber-300 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-amber-800 shadow-sm">
                <ClockIcon className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-900">
                  {t("dashboard.company_guide.alert_hours_title")}
                </div>
                <div className="text-xs text-slate-600">
                  {t("dashboard.company_guide.alert_hours_subtitle", { count: hoursIssues.length })}
                </div>
              </div>
            </div>
            <span className="inline-flex shrink-0 items-center gap-1 text-sm font-semibold text-slate-900 underline decoration-slate-300 underline-offset-4">
              {t("dashboard.company_guide.alert_cta")}
              <ArrowRightIcon className="h-4 w-4" />
            </span>
          </Link>
        ) : null}
      </div>
    </RoleOnly>
  );
};
